import { Button, Typography } from '@epilot/concorde-elements'
import type { EpilotControlProps } from '@epilot/journey-logic-commons'
import { isBlockUiSchema } from '@epilot/journey-logic-commons'
import { memo, useRef } from 'react'
import { ErrorBoundary, useErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'

import { BlockContent } from '../concorde-renderers/layouts/components'

export function withBlockErrorBoundary<P extends EpilotControlProps>(
  WrappedComponent: React.ComponentType<P>
) {
  const wrapper = (props: P) => {
    const isBlock = isBlockUiSchema(props?.uischema)
    const isNested = Boolean(isBlock && props.uischema.options?.isNested)
    const blockId = isBlock ? props.uischema.id : undefined

    return (
      <BlockErrorBoundary blockId={blockId} isNested={isNested}>
        <WrappedComponent {...props} />
      </BlockErrorBoundary>
    )
  }

  wrapper.displayName = `withBlockErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`

  return wrapper
}

type BlockErrorBoundaryProps = React.PropsWithChildren<
  HumanFriendlyErrorProps & {
    blockId?: string
  }
>

export const BlockErrorBoundary = memo(
  ({ children, blockId, ...props }: BlockErrorBoundaryProps) => {
    const retries = useRef<number>(0)

    return (
      <ErrorBoundary
        FallbackComponent={() => <HumanFriendlyError {...props} />}
        onError={(originalError) => {
          const error = new Error(
            'An error caused a block to malfunction and break'
          )

          error.cause = originalError

          // eslint-disable-next-line no-console
          console.error(error, { blockId, attempts: retries.current })
        }}
        onReset={() => {
          retries.current += 1 // increment attempts
        }}
        resetKeys={blockId ? ['block', blockId] : undefined}
      >
        {children}
      </ErrorBoundary>
    )
  }
)

BlockErrorBoundary.displayName = 'BlockErrorBoundary'

type HumanFriendlyErrorProps = {
  isNested: boolean
}

const HumanFriendlyError = (props: HumanFriendlyErrorProps) => {
  const { t } = useTranslation()
  const { resetBoundary } = useErrorBoundary()

  return (
    <BlockContent isNested={props.isNested} showCard={false}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '1rem'
        }}
      >
        <Typography variant="error">
          {t(
            'blocks.common.error_boundary.human_friendly_error',
            'Sorry, an error occurred with this block.'
          )}
        </Typography>
        <Button onClick={resetBoundary} variant="ghost-danger">
          {t('blocks.common.error_boundary.retry_btn', 'Retry')}
        </Button>
      </div>
    </BlockContent>
  )
}
